import React, { useState, useEffect } from 'react';
import {
  FaTrophy, FaMedal, FaStar, FaGraduationCap,
  FaCalendarCheck, FaChartLine, FaBook, FaClock,
  FaLock, FaUnlock
} from 'react-icons/fa';
import axios from 'axios';
import './Achievements.css';
import config from '../../config';

//const API_BASE_URL = 'http://localhost:3001/api/study-tools';
const API_BASE_URL = `${config.API_BASE_URL}/api/study-tools`;

const Achievements = () => {
  const [achievements, setAchievements] = useState([]);
  const [userStats, setUserStats] = useState({
    totalStudyHours: 0,
    longestStreak: 0,
    completedTasks: 0,
    averageProductivity: 0
  });
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch achievements
      const achievementsResponse = await axios.get(`${API_BASE_URL}/achievements`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAchievements(achievementsResponse.data);

      // Fetch analytics for stats
      const analyticsResponse = await axios.get(`${API_BASE_URL}/analytics`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setUserStats({
        totalStudyHours: analyticsResponse.data.totalStudyHours,
        longestStreak: analyticsResponse.data.longestStreak,
        completedTasks: analyticsResponse.data.completedTasks || 0,
        averageProductivity: analyticsResponse.data.averageProductivity
      });
    } catch (error) {
      console.error('Error fetching achievements data:', error);
      setError('Failed to load achievements. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getProgressColor = (progress) => {
    if (progress < 30) return '#e74c3c';
    if (progress < 70) return '#f1c40f';
    return '#2ecc71';
  };

  const filteredAchievements = achievements.filter(achievement => 
    selectedCategory === 'all' || achievement.category === selectedCategory
  );

  const categories = [
    { id: 'all', name: 'All Achievements', icon: <FaTrophy /> },
    { id: 'milestones', name: 'Milestones', icon: <FaMedal /> },
    { id: 'streaks', name: 'Streaks', icon: <FaStar /> },
    { id: 'tasks', name: 'Tasks', icon: <FaChartLine /> },
    { id: 'resources', name: 'Resources', icon: <FaBook /> }
  ];

  if (loading) {
    return (
      <div className="achievements-container">
        <div className="loading-spinner">Loading achievements...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="achievements-container">
        <div className="error-message">
          {error}
          <button onClick={fetchData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="achievements-container">
      <div className="achievements-header">
        <h2>Achievements</h2>
        <div className="user-level">
          <div className="level-badge">
            <FaTrophy />
            <span>Level {Math.floor(userStats.totalStudyHours / 10) + 1}</span>
          </div>
        </div>
      </div>

      <div className="stats-overview">
        <div className="stat-card">
          <FaClock className="stat-icon" />
          <div className="stat-info">
            <h3>Total Study Hours</h3>
            <div className="stat-value">{Math.round(userStats.totalStudyHours)}</div>
          </div>
        </div>
        <div className="stat-card">
          <FaCalendarCheck className="stat-icon" />
          <div className="stat-info">
            <h3>Longest Streak</h3>
            <div className="stat-value">{userStats.longestStreak} days</div>
          </div>
        </div>
        <div className="stat-card">
          <FaChartLine className="stat-icon" />
          <div className="stat-info">
            <h3>Tasks Completed</h3>
            <div className="stat-value">{userStats.completedTasks}</div>
          </div>
        </div>
        <div className="stat-card">
          <FaStar className="stat-icon" />
          <div className="stat-info">
            <h3>Avg. Productivity</h3>
            <div className="stat-value">{Math.round(userStats.averageProductivity)}%</div>
          </div>
        </div>
      </div>

      <div className="achievements-categories">
        {categories.map(category => (
          <button
            key={category.id}
            className={`category-btn ${selectedCategory === category.id ? 'active' : ''}`}
            onClick={() => setSelectedCategory(category.id)}
          >
            {category.icon}
            <span>{category.name}</span>
          </button>
        ))}
      </div>

      <div className="achievements-grid">
        {filteredAchievements.length > 0 ? (
          filteredAchievements.map(achievement => (
            <div 
              key={achievement.id} 
              className={`achievement-card ${achievement.completed ? 'completed' : ''}`}
            >
              <div className="achievement-icon">
                {achievement.icon}
                {achievement.completed ? (
                  <FaUnlock className="lock-icon unlocked" />
                ) : (
                  <FaLock className="lock-icon" />
                )}
              </div>
              <div className="achievement-content">
                <h3>{achievement.title}</h3>
                <p>{achievement.description}</p>
                <div className="achievement-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{
                        width: `${achievement.progress}%`,
                        backgroundColor: getProgressColor(achievement.progress)
                      }}
                    />
                  </div>
                  <span className="progress-text">{achievement.progress}%</span>
                </div>
                <div className="achievement-reward">
                  <FaMedal className="reward-icon" />
                  <span>{achievement.reward}</span>
                </div>
                {achievement.completed && achievement.dateCompleted && (
                  <div className="completion-date">
                    Completed on {new Date(achievement.dateCompleted).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="no-achievements-message">
            No achievements found for this category. Keep studying to unlock new achievements!
          </div>
        )}
      </div>

      <div className="achievements-summary">
        <h3>Progress Summary</h3>
        <div className="summary-stats">
          <div className="summary-item">
            <span>Total Achievements</span>
            <strong>{achievements.length}</strong>
          </div>
          <div className="summary-item">
            <span>Completed</span>
            <strong>
              {achievements.filter(a => a.completed).length} / {achievements.length}
            </strong>
          </div>
          <div className="summary-item">
            <span>Completion Rate</span>
            <strong>
              {achievements.length > 0
                ? Math.round((achievements.filter(a => a.completed).length / achievements.length) * 100)
                : 0}%
            </strong>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Achievements; 